import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { format, parseISO, isAfter } from "date-fns";
import { sv } from "date-fns/locale";
import { Card, Badge, Spinner, Alert } from "react-bootstrap";
import axios from "../../../../../services/AxiosInstance";
import { getApiUrl } from "../../../../../utils/ApiUtils";
import PageTitle from "../../../../layouts/PageTitle";
import StatusTimeline from "./StatusTimelineBootstrap";
import CustomerDetailsCardBootstrap from "./CustomerDetailsCardBootstrap";
import InvoiceStatusCardBootstrap from "./InvoiceStatusCardBootstrap";
import InvoicePreviewCardBootstrap from "./InvoicePreviewCardBootstrap";
import InvoiceNotesList from "../Notes/InvoiceNotesList";
import Swal from "sweetalert2";
import { Button, Dropdown, ButtonGroup } from "react-bootstrap";
import { toast } from "sonner";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getStatusColor } from '../../../../../config/invoiceStatus';
import {
  faFileInvoice,
  faUser,
  faBuilding,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faCalendarAlt,
  faArrowLeft,
  faDownload,
  faPrint,
  faBell,
  faBan,
  faPlus,
  faCreditCard,
  faEllipsisV,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faEuroSign,
  faReceipt,
  faLock,
  faUnlock,
  faEye,
  faHistory,
  faChevronDown,
  faHourglassHalf
} from '@fortawesome/free-solid-svg-icons';

const InvoiceDetailBootstrap = () => {
  const history = useHistory();
  const { companyId, invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPaid, setTotalPaid] = useState(0);
  const [combinedTimeline, setCombinedTimeline] = useState([]);
  const invoiceRef = useRef(null);
  const [customerData, setCustomerData] = useState(null);
  const [showPdf, setShowPdf] = useState(false);

  // Constants for document types
  const DocumentType = {
    INVOICE: 'invoice',
    CREDIT_NOTE: 'credit_note'
  };

  // Constants for status types (matches backend enum)
  const StatusType = {
    DRAFT: 'Draft',
    PUBLISHED: 'Published',
    PARTIALLY_PAID: 'Partially_Paid',  // NEW
    PAID: 'Paid',
    APPLIED: 'Applied',
    REFUNDED: 'Refunded',  // UPDATED enum value
    OVERDUE: 'Overdue',
    CANCELLED: 'Cancelled'
  };

  // Fetch all invoice data including verifications
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // Fetch invoice details first to get customer ID
        const invoiceResponse = await axios.get(
          getApiUrl(`${companyId}/invoices/${invoiceId}/details`),
          { withCredentials: true }
        );

        const invoiceData = invoiceResponse.data.data;
        const customerId = invoiceData.customerid;

        // Now fetch all data in parallel including customer details and verifications
        const [statusHistoryResponse, paymentsResponse, customerResponse, verificationsResponse] = await Promise.all([
          axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/status-history`), { withCredentials: true })
            .catch(error => {
              console.warn("Failed to fetch status history:", error);
              return { data: { data: [] } };
            }),
          axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/payments`), { withCredentials: true })
            .catch(error => {
              console.warn("Failed to fetch payments:", error);
              return { data: { data: [] } };
            }),
          customerId ?
            axios.get(getApiUrl(`${companyId}/sales/customers/${customerId}`), { withCredentials: true })
              .catch(error => {
                console.warn("Failed to fetch customer details:", error);
                return { data: null };
              })
            : Promise.resolve({ data: null }),
          // Fetch verifications for this invoice
          axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/verifications`), { withCredentials: true })
            .catch(error => {
              console.warn("Failed to fetch verifications:", error);
              return { data: { data: [] } };
            })
        ]);

        // Extract data
        const statusHistoryData = statusHistoryResponse.data.data || [];
        const paymentsData = paymentsResponse.data.data || [];
        const customerData = customerResponse?.data;
        const verificationsData = verificationsResponse.data.data || [];

        console.log("Invoice data:", invoiceData);
        console.log("Customer data:", customerData);
        console.log("Status history:", statusHistoryData);
        console.log("Payments:", paymentsData);
        console.log("Verifications:", verificationsData);

        // Set state with fetched data
        setInvoice(invoiceData);
        setStatusHistory(statusHistoryData);
        setPaymentHistory(paymentsData);
        setCustomerData(customerData);
        setVerifications(verificationsData);
        setTotalPaid(paymentsData.reduce((sum, p) => sum + parseFloat(p.amount_paid), 0));

        // Combine timeline events including verifications
        const combinedEvents = [];

        // Add status events
        statusHistoryData.forEach(status => {
          combinedEvents.push({
            date: parseISO(status.changed_at),
            type: 'status',
            status: status.status,
            displayName: status.status,
            icon: faCheckCircle
          });
        });

        // Add payment events
        paymentsData.forEach(payment => {
          combinedEvents.push({
            date: parseISO(payment.created_at || payment.payment_date),
            type: 'payment',
            amount: parseFloat(payment.amount_paid),
            verificationNumber: payment.verification_number,
            paymentMethod: payment.payment_method || 'Bank transfer',
            status: 'Payment',
            isPayment: true,
            icon: faCreditCard
          });
        });

        // Add verification events
        verificationsData.forEach(verification => {
          combinedEvents.push({
            date: parseISO(verification.created_at),
            type: 'verification',
            verificationId: verification.verification_id,
            verificationNumber: `${verification.series}${verification.number}`,
            description: verification.description,
            referenceType: verification.reference_type,
            status: 'Verification',
            isLocked: verification.is_locked,
            icon: faReceipt
          });
        });

        // Sort by date (newest first)
        combinedEvents.sort((a, b) => b.date.getTime() - a.date.getTime());
        setCombinedTimeline(combinedEvents);

        console.log("Combined timeline:", combinedEvents);

        // Fetch PDF after everything else is loaded
        fetchPdf();
      } catch (error) {
        console.error("Error fetching invoice data:", error);
        setError("Kunde inte hämta fakturadetaljer. Vänligen försök igen senare.");
      } finally {
        setLoading(false);
      }
    }

    if (invoiceId && companyId) {
      fetchData();
    }
  }, [companyId, invoiceId]);

  const fetchPdf = async () => {
    try {
      const response = await axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/pdf`), {
        responseType: 'blob',
        withCredentials: true,
      });

      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      setPdfData(fileURL);
    } catch (error) {
      console.error('Error fetching the PDF:', error);
    }
  };

  // Helper function to get verification type display name
  const getVerificationTypeDisplayName = (referenceType) => {
    const typeNames = {
      'customer_invoice_payment': 'Kundbetalning',
      'credit_note_refund_payment': 'Återbetalning',
      'verification_reversal': 'Återföring'
    };
    return typeNames[referenceType] || referenceType;
  };

  // Function to view verification details
  const handleViewVerification = (verificationId) => {
    // Navigate to verification detail page
    history.push(`/${companyId}/bookkeeping/verifications/${verificationId}`);
  };

  const handleRegisterPayment = () => {
    Swal.fire({
      title: 'Registrera betalning',
      html: `
      <form id="payment-form" class="text-start">
        <div class="mb-3">
          <label for="payment-amount" class="form-label">Belopp</label>
          <div class="input-group">
            <input 
              type="number" 
              class="form-control" 
              id="payment-amount" 
              value="${remainingAmount.toFixed(2)}" 
              min="0" 
              max="${remainingAmount.toFixed(2)}" 
              step="0.01"
            />
            <span class="input-group-text">SEK</span>
          </div>
        </div>
        <div class="mb-3">
          <label for="payment-date" class="form-label">Betalningsdatum</label>
          <input 
            type="date" 
            class="form-control" 
            id="payment-date" 
            value="${format(new Date(), 'yyyy-MM-dd')}" 
          />
        </div>
        <div class="mb-3">
          <label for="payment-method" class="form-label">Betalning mottagen på konto</label>
          <select class="form-select" id="payment-method">
            <option value="1930">Bankkonto</option>
            <option value="1910">Kontant</option>
            <option value="1940">Swish</option>
          </select>
        </div>
      </form>
    `,
      showCancelButton: true,
      confirmButtonText: 'Registrera betalning',
      cancelButtonText: 'Avbryt',
      preConfirm: () => {
        const amount = document.getElementById('payment-amount').value;
        const date = document.getElementById('payment-date').value;
        const method = document.getElementById('payment-method').value;

        if (!amount || !date) {
          Swal.showValidationMessage('Vänligen fyll i alla obligatoriska fält');
          return false;
        }

        return { amount, date, method };
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const { amount, date, method } = result.value;

        axios.post(
          getApiUrl(`${companyId}/invoices/${invoiceId}/register-payment`),
          {
            paidAmount: parseFloat(amount),
            paymentDate: date,
            selectedAccount: method,
          },
          { withCredentials: true }
        )
          .then(() => {
            // Show success toast
            toast.success('Betalning registrerad!', {
              description: 'Fakturan har markerats som betald.',
              duration: 3000,
            });

            // Reload the page after a short delay to show the toast
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(error => {
            console.error("Error registering payment:", error);
            toast.error('Fel!', {
              description: 'Kunde inte registrera betalningen. Försök igen senare.',
              duration: 3000,
            });
          });
      }
    });
  };

  const handleCreateCreditNote = () => {
    history.push(`/${companyId}/create-credit-note/${invoice.invoiceid || invoice.id}`);
  };

  const handleSendReminder = () => {
    Swal.fire({
      title: 'Skicka påminnelse',
      text: `Vill du skicka en betalningspåminnelse för faktura #${invoice.invoicenumber}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Ja, skicka påminnelse',
      cancelButtonText: 'Avbryt'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.post(
          getApiUrl(`${companyId}/invoices/${invoiceId}/send-reminder`),
          {},
          { withCredentials: true }
        )
          .then(() => {
            toast.success('Påminnelse skickad!', {
              description: 'Betalningspåminnelsen har skickats till kunden.'
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(error => {
            console.error("Error sending reminder:", error);
            toast.error('Fel!', {
              description: 'Kunde inte skicka påminnelsen. Försök igen senare.'
            });
          });
      }
    });
  };

  const handleCancelInvoice = () => {
    Swal.fire({
      title: 'Makulera faktura',
      text: `Är du säker på att du vill makulera faktura #${invoice.invoicenumber}? Detta kan inte ångras.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, makulera faktura',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.post(
          getApiUrl(`${companyId}/invoices/${invoiceId}/cancel`),
          {},
          { withCredentials: true }
        )
          .then(() => {
            toast.success('Fakturan makulerad!', {
              description: 'Fakturan har makulerats och kan inte längre betalas.'
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(error => {
            console.error("Error cancelling invoice:", error);
            toast.error('Fel!', {
              description: 'Kunde inte makulera fakturan. Försök igen senare.'
            });
          });
      }
    });
  };

  // Helper function for status display names (Swedish translations)
  const getStatusDisplayName = (status, documentType = 'invoice') => {
    const statusNames = {
      [DocumentType.INVOICE]: {
        [StatusType.DRAFT]: 'Utkast',
        [StatusType.PUBLISHED]: 'Publicerad',
        [StatusType.PARTIALLY_PAID]: 'Delvis betald',  // NEW
        [StatusType.PAID]: 'Betald',
        [StatusType.OVERDUE]: 'Förfallen',
        [StatusType.CANCELLED]: 'Makulerad',
        [StatusType.REFUNDED]: 'Återbetald',  // NEW
        'Reminder Sent': 'Påminnelse skickad'
      },
      [DocumentType.CREDIT_NOTE]: {
        [StatusType.DRAFT]: 'Utkast',
        [StatusType.PUBLISHED]: 'Publicerad',
        [StatusType.APPLIED]: 'Tillämpad på faktura',
        [StatusType.REFUNDED]: 'Återbetald till kund',
        [StatusType.CANCELLED]: 'Makulerad'
      }
    };

    return statusNames[documentType]?.[status] || status;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
  };

  // Get status color and icon (using centralized config for colors)
  const getStatusConfig = (status) => {
    const colorVariant = getStatusColor(status);

    // Map Bootstrap color variants to custom colors
    const colorMap = {
      'secondary': { color: '#6b7280', bg: '#f3f4f6' },
      'primary': { color: '#3b82f6', bg: '#eff6ff' },
      'warning': { color: '#f59e0b', bg: '#fffbeb' },  // Orange for Partially_Paid
      'success': { color: '#059669', bg: '#f0fdf4' },
      'danger': { color: '#dc2626', bg: '#fef2f2' },
      'info': { color: '#8b5cf6', bg: '#f5f3ff' }  // Purple for Refunded
    };

    const colors = colorMap[colorVariant] || colorMap['secondary'];

    const configs = {
      'Draft': { ...colors, icon: faClock },
      'Published': { ...colorMap['primary'], icon: faFileInvoice },
      'Partially_Paid': { ...colorMap['warning'], icon: faHourglassHalf },  // NEW
      'Paid': { ...colorMap['success'], icon: faCheckCircle },
      'Overdue': { ...colorMap['danger'], icon: faExclamationTriangle },
      'Cancelled': { ...colorMap['secondary'], icon: faBan },
      'Refunded': { ...colorMap['info'], icon: faReceipt }  // NEW
    };

    return configs[status] || { color: '#6b7280', bg: '#f3f4f6', icon: faFileInvoice };
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar fakturadetaljer...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invoice-detail-container">
        <Alert variant="danger" className="text-center">
          <h5>Ett fel uppstod</h5>
          <p>{error}</p>
          <Link
            to={`/${companyId}/invoices`}
            className="btn btn-outline-danger btn-modern"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Tillbaka till fakturalistan
          </Link>
        </Alert>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="invoice-detail-container">
        <Alert variant="warning" className="text-center">
          <h5>Faktura hittades inte</h5>
          <p>Den begärda fakturan kunde inte hittas.</p>
          <Link
            to={`/${companyId}/invoices`}
            className="btn btn-outline-warning btn-modern"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Tillbaka till fakturalistan
          </Link>
        </Alert>
      </div>
    );
  }

  // Calculate remaining amount and effective amount
  const totalCreditNoteAmount = (invoice.credit_notes || []).reduce(
    (sum, cn) => sum + Math.abs(parseFloat(cn.amount)), 0);

  const effectiveAmount = invoice.totals.grand_total - totalCreditNoteAmount;
  const remainingAmount = effectiveAmount - totalPaid;
  const statusConfig = getStatusConfig(invoice.status);

  // Calculate days until due date
  const today = new Date();
  const dueDate = parseISO(invoice.deadline);
  const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

  return (
    <>
      <style>{`
        .invoice-detail-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1rem;
        }
        
        .invoice-header {
          margin-bottom: 2rem;
        }

        .back-link {
          color: #6366f1 !important;
          text-decoration: none !important;
          transition: all 0.2s ease;
          font-weight: 500;
          font-size: 0.875rem;
        }

        .back-link:hover {
          color: #5856eb !important;
          transform: translateX(-2px);
        }

        .invoice-number {
          font-size: 1.75rem;
          font-weight: 800;
          margin-bottom: 0.25rem;
          color: #1f2937;
          line-height: 1;
        }

        .invoice-subtitle {
          color: #6b7280;
          font-size: 0.875rem;
          margin-bottom: 1rem;
          font-weight: 400;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-weight: 600;
          font-size: 0.75rem;
          margin-bottom: 1rem;
        }

        .customer-info-header {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1rem;
          margin-top: 1rem;
        }

        .customer-name-header {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .customer-org-header {
          color: #64748b;
          font-size: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .customer-details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.75rem;
        }

        .customer-detail-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          color: #475569;
        }

        .customer-detail-icon {
          width: 14px;
          height: 14px;
          color: #6366f1;
          flex-shrink: 0;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
          margin-bottom: 2rem;
        }
        
        .stat-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--accent-color, #3b82f6), var(--accent-color-light, #60a5fa));
        }
        
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }
        
        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.125rem;
          position: relative;
        }

        .stat-icon::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 12px;
          background: inherit;
          opacity: 0.1;
        }
        
        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.375rem;
          color: #1f2937;
        }
        
        .stat-label {
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        
        .main-content {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          margin-bottom: 2rem;
        }
        
        .content-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .content-card:hover {
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
        }
        
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #f3f4f6;
        }
        
        .card-title {
          font-size: 1rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .card-icon {
          width: 18px;
          height: 18px;
          color: #6366f1;
        }
        
        .action-buttons {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .btn-group .btn-modern {
          border-radius: 0;
        }

        .btn-group .btn-modern:first-child {
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
          border-right: none;
        }

        .btn-group .btn-modern:last-child {
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;
          border-left: 1px solid rgba(255, 255, 255, 0.2);
        }

        .btn-group .btn-modern:hover {
          transform: none;
        }

        .btn-group:hover .btn-modern {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        }

        .dropdown-toggle::after {
          margin-left: 0;
        }
        
        .btn-modern {
          border-radius: 8px;
          padding: 0.75rem 1rem;
          font-weight: 600;
          font-size: 0.8125rem;
          display: flex;
          align-items: center;
          gap: 0.375rem;
          transition: all 0.3s ease;
          border: none;
          position: relative;
          overflow: hidden;
        }

        .btn-modern::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          transform: translateX(-100%);
          transition: transform 0.6s ease;
        }

        .btn-modern:hover::before {
          transform: translateX(100%);
        }
        
        .btn-modern:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
        }

        .btn-primary-modern {
          background: var(--bs-primary);
          color: white;
        }

        .btn-primary-modern:hover {
          background: var(--bs-primary);
          color: white;
        }

        .timeline-compact {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .timeline-item-compact {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 0.875rem;
          background: #f8fafc;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .timeline-item-compact:hover {
          border-color: #6366f1;
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.1);
        }

        .timeline-icon-compact {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .timeline-content-compact {
          flex: 1;
          min-width: 0;
        }

        .timeline-title-compact {
          font-weight: 600;
          color: #1e293b;
          font-size: 0.8125rem;
          margin-bottom: 0.125rem;
          line-height: 1.3;
        }

        .timeline-date-compact {
          color: #64748b;
          font-size: 0.6875rem;
          font-weight: 500;
        }

        .timeline-description-compact {
          color: #475569;
          font-size: 0.75rem;
          margin-top: 0.375rem;
          line-height: 1.4;
        }

        .timeline-badge-compact {
          font-size: 0.6875rem;
          padding: 0.1875rem 0.375rem;
          border-radius: 8px;
          font-weight: 500;
          white-space: nowrap;
        }

        .pdf-section {
          margin-top: 2rem;
        }

        .pdf-toggle {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-bottom: 1rem;
        }

        .pdf-toggle:hover {
          border-color: #6366f1;
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.1);
        }

        .pdf-toggle-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .pdf-toggle-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .pdf-preview-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .pdf-viewer {
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .pdf-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 200px;
          color: #6b7280;
          font-size: 0.8125rem;
        }
        
        @media (max-width: 1024px) {
          .invoice-header {
            padding: 1.5rem;
          }
          
          .invoice-number {
            font-size: 1.875rem;
          }
          
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          }
          
          .action-buttons {
            flex-direction: column;
          }
          
          .btn-modern {
            justify-content: center;
          }

          .content-card {
            padding: 1.25rem;
          }

          .customer-details-grid {
            grid-template-columns: 1fr;
            gap: 0.5rem;
          }
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr 1fr;
          }
          
          .invoice-number {
            font-size: 1.75rem;
          }

          .timeline-item-compact {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>

      <PageTitle activeMenu="Fakturadetaljer" motherMenu="Fakturor" />

      <div className="invoice-detail-container">
        {/* Back Button - Top Left */}
        <div className="mb-3">
          <Button
            variant="link"
            className="p-0 text-decoration-none d-flex align-items-center gap-2"
            onClick={() => history.push(`/${companyId}/invoices`)}
            style={{ color: '#64748b', fontSize: '0.875rem' }}
          >
            <FontAwesomeIcon icon={faArrowLeft} size="sm" />
            Tillbaka till listan
          </Button>
        </div>

        {/* Enhanced Header with Customer Info */}
        <div className="invoice-header">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h2 className="mb-2" style={{ fontWeight: '700' }}>Faktura {invoice.invoicenumber}</h2>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div className="d-flex align-items-center gap-2 text-muted">
                  <FontAwesomeIcon icon={customerData?.type === 'company' ? faBuilding : faUser} size="sm" />
                  <span style={{ fontWeight: '500' }}>{customerData?.name || invoice.customername}</span>
                </div>
                <div className="d-flex align-items-center gap-2" style={{
                  padding: '0.25rem 0.75rem',
                  background: '#f8fafc',
                  borderRadius: '6px',
                  fontSize: '0.8125rem'
                }}>
                  <FontAwesomeIcon icon={faCalendarAlt} size="sm" className="text-muted" />
                  <span className="text-muted">Fakturadatum:</span>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>
                    {format(parseISO(invoice.invoicedate), 'dd MMM yyyy', { locale: sv })}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2" style={{
                  padding: '0.25rem 0.75rem',
                  background: daysUntilDue < 0 ? '#fef2f2' : daysUntilDue <= 7 ? '#fffbeb' : '#f0fdf4',
                  borderRadius: '6px',
                  fontSize: '0.8125rem'
                }}>
                  <FontAwesomeIcon
                    icon={faHourglassHalf}
                    size="sm"
                    style={{
                      color: daysUntilDue < 0 ? '#ef4444' : daysUntilDue <= 7 ? '#f59e0b' : '#10b981'
                    }}
                  />
                  <span className="text-muted">Förfaller:</span>
                  <span style={{
                    fontWeight: '600',
                    color: daysUntilDue < 0 ? '#ef4444' : daysUntilDue <= 7 ? '#f59e0b' : '#10b981'
                  }}>
                    {format(parseISO(invoice.deadline), 'dd MMM yyyy', { locale: sv })}
                  </span>
                  {daysUntilDue < 0 && (
                    <Badge bg="danger" style={{ fontSize: '0.625rem', marginLeft: '0.25rem' }}>
                      {Math.abs(daysUntilDue)}d försenad
                    </Badge>
                  )}
                  {daysUntilDue >= 0 && daysUntilDue <= 7 && (
                    <Badge bg="warning" text="dark" style={{ fontSize: '0.625rem', marginLeft: '0.25rem' }}>
                      {daysUntilDue}d kvar
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              <Badge
                style={{
                  color: statusConfig.color,
                  backgroundColor: statusConfig.bg,
                  borderColor: statusConfig.color + '40',
                  border: `1px solid`,
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                <FontAwesomeIcon icon={statusConfig.icon} className="me-2" />
                {getStatusDisplayName(invoice.status)}
              </Badge>
              <div className="action-buttons">
                <Dropdown as={ButtonGroup}>
                  <Button
                    className="btn btn-primary-modern btn-modern"
                    onClick={handleRegisterPayment}
                    disabled={invoice.status === 'Paid'}
                  >
                    <FontAwesomeIcon icon={faCreditCard} />
                    Registrera betalning
                  </Button>

                  <Dropdown.Toggle
                    split
                    className="btn btn-primary-modern btn-modern"
                    id="invoice-actions-dropdown"
                    disabled={invoice.status === 'Paid'}
                  />

                  <Dropdown.Menu align="end">
                    {invoice.status !== 'Draft' && (
                      <>
                        <Dropdown.Item onClick={handleCreateCreditNote}>
                          <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                          Skapa kreditnota
                        </Dropdown.Item>

                        {new Date() > parseISO(invoice.deadline) && invoice.status !== 'Paid' && (
                          <Dropdown.Item onClick={handleSendReminder}>
                            <FontAwesomeIcon icon={faBell} className="me-2" />
                            Skicka påminnelse
                          </Dropdown.Item>
                        )}

                        <Dropdown.Divider />
                      </>
                    )}

                    <Dropdown.Item
                      onClick={() => window.open(pdfData, '_blank')}
                      disabled={!pdfData}
                    >
                      <FontAwesomeIcon icon={faDownload} className="me-2" />
                      Ladda ner PDF
                    </Dropdown.Item>

                    {invoice.status !== 'Cancelled' && invoice.status !== 'Draft' && (
                      <>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={handleCancelInvoice}
                          className="text-danger"
                        >
                          <FontAwesomeIcon icon={faBan} className="me-2" />
                          Makulera faktura
                        </Dropdown.Item>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>

        {/* Compact Statistics */}
        <div className="stats-grid">
          <div className="stat-card" style={{ '--accent-color': '#0284c7', '--accent-color-light': '#0ea5e9' }}>
            <div className="stat-icon" style={{ background: '#f0f9ff', color: '#0284c7' }}>
              <FontAwesomeIcon icon={faEuroSign} />
            </div>
            <div className="stat-value" style={{ color: '#0284c7' }}>
              {formatCurrency(invoice.totals.grand_total)}
            </div>
            <div className="stat-label">Totalt belopp</div>
          </div>

          <div className="stat-card" style={{ '--accent-color': '#059669', '--accent-color-light': '#10b981' }}>
            <div className="stat-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <div className="stat-value" style={{ color: '#059669' }}>
              {formatCurrency(totalPaid)}
            </div>
            <div className="stat-label">Betalt belopp</div>
          </div>

          <div className="stat-card" style={{ '--accent-color': '#d97706', '--accent-color-light': '#f59e0b' }}>
            <div className="stat-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div className="stat-value" style={{ color: '#d97706' }}>
              {formatCurrency(remainingAmount)}
            </div>
            <div className="stat-label">Kvarstående belopp</div>
          </div>

          <div className="stat-card" style={{ '--accent-color': '#9333ea', '--accent-color-light': '#a855f7' }}>
            <div className="stat-icon" style={{ background: '#faf5ff', color: '#9333ea' }}>
              <FontAwesomeIcon icon={faCalendarAlt} />
            </div>
            <div className="stat-value" style={{ color: '#9333ea' }}>
              {Math.ceil((parseISO(invoice.deadline) - new Date()) / (1000 * 60 * 60 * 24))}
            </div>
            <div className="stat-label">Dagar kvar</div>
          </div>
        </div>

        {/* Main Content - Full Width Timeline */}
        <div className="main-content">
          {/* Compact Timeline with Integrated Verifications */}
          <div className="content-card">
            <div className="card-header">
              <h3 className="card-title">
                <FontAwesomeIcon icon={faHistory} className="card-icon" />
                Fakturahistorik
              </h3>
              <Badge bg="primary" style={{ fontSize: '0.6875rem' }}>{combinedTimeline.length} händelser</Badge>
            </div>

            <div className="timeline-compact">
              {combinedTimeline.length > 0 ? (
                combinedTimeline.map((event, index) => (
                  <div
                    key={index}
                    className="timeline-item-compact"
                    onClick={event.type === 'verification' ? () => handleViewVerification(event.verificationId) : undefined}
                    style={{ cursor: event.type === 'verification' ? 'pointer' : 'default' }}
                  >
                    <div
                      className="timeline-icon-compact"
                      style={{
                        background: event.type === 'status' ? '#f0f9ff' :
                          event.type === 'payment' ? '#f0fdf4' : '#fef3f2',
                        color: event.type === 'status' ? '#0284c7' :
                          event.type === 'payment' ? '#059669' : '#dc2626'
                      }}
                    >
                      <FontAwesomeIcon icon={event.icon} />
                    </div>

                    <div className="timeline-content-compact">
                      <div className="d-flex justify-content-between align-items-center">
                        <h4 className="timeline-title-compact">
                          {event.type === 'status' && `Status: ${getStatusDisplayName(event.status)}`}
                          {event.type === 'payment' && 'Betalning registrerad'}
                          {event.type === 'verification' && `Verifikat ${event.verificationNumber}`}
                        </h4>

                        <div className="d-flex align-items-center gap-2">
                          {event.type === 'payment' && (
                            <span className="timeline-badge-compact" style={{ background: '#f0fdf4', color: '#059669' }}>
                              {formatCurrency(event.amount)}
                            </span>
                          )}
                          {event.type === 'verification' && (
                            <span className="timeline-badge-compact" style={{
                              background: event.isLocked ? '#fef2f2' : '#f0f9ff',
                              color: event.isLocked ? '#dc2626' : '#0284c7'
                            }}>
                              <FontAwesomeIcon icon={event.isLocked ? faLock : faUnlock} className="me-1" />
                              {event.isLocked ? 'Låst' : 'Öppen'}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="timeline-date-compact">
                        {format(event.date, 'dd MMM yyyy • HH:mm', { locale: sv })}
                      </div>

                      {(event.type === 'verification' && event.description) && (
                        <div className="timeline-description-compact">
                          {event.description}
                          <br />
                          <small>Typ: {getVerificationTypeDisplayName(event.referenceType)}</small>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted">
                  <FontAwesomeIcon icon={faHistory} size="2x" className="mb-3 opacity-50" />
                  <p style={{ fontSize: '0.875rem' }}>Ingen historik tillgänglig ännu</p>
                </div>
              )}
            </div>
          </div>

          {/* Invoice Notes Section */}
          <div className="content-card">
            <InvoiceNotesList invoiceId={invoiceId} />
          </div>
        </div>

        {/* PDF Section - Moved to bottom with toggle */}
        <div className="pdf-section">
          <div className="pdf-toggle" onClick={() => setShowPdf(!showPdf)}>
            <div className="pdf-toggle-content">
              <h3 className="pdf-toggle-title">
                <FontAwesomeIcon icon={faFileInvoice} />
                Visa fakturaförhandsvisning
              </h3>
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{
                  transform: showPdf ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  fontSize: '0.75rem'
                }}
              />
            </div>
          </div>

          {showPdf && (
            <div className="pdf-preview-card">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="h6 mb-0">PDF-förhandsvisning</h4>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => window.open(pdfData, '_blank')}
                    disabled={!pdfData}
                    style={{ fontSize: '0.75rem' }}
                  >
                    <FontAwesomeIcon icon={faDownload} className="me-1" />
                    Ladda ner
                  </button>

                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => window.print()}
                    style={{ fontSize: '0.75rem' }}
                  >
                    <FontAwesomeIcon icon={faPrint} className="me-1" />
                    Skriv ut
                  </button>
                </div>
              </div>

              <div className="pdf-viewer">
                {pdfData ? (
                  <iframe
                    src={pdfData}
                    width="100%"
                    height="600"
                    style={{ border: 'none' }}
                    title="Faktura förhandsvisning"
                  />
                ) : (
                  <div className="pdf-loading">
                    <div className="text-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Laddar PDF förhandsvisning...
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default InvoiceDetailBootstrap;
